<template>
  <ProgressCircular v-if="loading" />
  <form v-else novalidate ref="updatePlaceForm" @submit.prevent="() => {}">
    <v-row no-gutters justify="space-between">
      <v-col cols="8">
        <v-card max-width="1200" flat color="transparent">
          <v-card-title class="mb-10">
            <FormHeader
              :loading="loading"
              :link="'page-places'"
              :title="data.title ? `${data.title} место` : `Создать место`"
            />
          </v-card-title>
          <v-tabs v-model="activeTab">
            <v-tab v-for="tab in $options.tabs" :key="tab.value">
              {{ tab.title }}
            </v-tab>
          </v-tabs>
          <v-tabs-items v-model="activeTab">
            <v-tab-item>
              <PageContent>
                <v-card-text>
                  <v-container>
                    <InputTitleSlug
                      :is-loading="loading"
                      :title="fields.title"
                      :title-error="validationErrors.title"
                      :slug="fields.slug"
                      :slug-error="validationErrors.slug"
                      :isSlugEnabled="!isNew"
                      @change:title="fields.title = $event"
                      @change:slug="fields.slug = $event"
                      @input:slug="$v.fields.slug.$touch"
                    />
                    <v-row>
                      <v-col cols="12">
                        <span>Подзаголовок (<TranslationIcon />)</span>
                        <v-text-field
                          outlined
                          :disabled="loading"
                          v-model="fields.subtitle"
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12">
                        <MediaFilePicker
                          :label="'Обложка'"
                          :multiple="false"
                          :file-type="$options.mediaTypesByKeys.IMAGE"
                          :value="fields.coverImageId"
                          @change="fields.coverImageId = $event"
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12">
                        <MediaFilePicker
                          :label="'Миниатюра'"
                          :multiple="false"
                          :file-type="$options.mediaTypesByKeys.IMAGE"
                          :value="fields.thumbnailId"
                          @change="fields.thumbnailId = $event"
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12">
                        <MediaFilePicker
                          :label="'Изображения'"
                          :file-type="$options.mediaTypesByKeys.IMAGE"
                          :value="fields.imageIds"
                          @change="fields.imageIds = $event"
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12">
                        <span>Место проведения*</span>
                        <LocationAutocomplete
                          id="address"
                          :address="fields.address"
                          :is-loading="loading"
                          :error-messages="validationErrors.address"
                          @set-location="fields.address = $event"
                          @update-location-part="updateLocation"
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12">
                        <SeoSettings
                          :data="data"
                          :loading="loading"
                          @on-input-seo="fields = Object.assign({}, fields, $event)"
                        />
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card-text>
              </PageContent>
            </v-tab-item>
            <v-tab-item>
              <PageContent>
                <v-card-text>
                  <DynamicListWrapper
                    :list="fields.widgets"
                    :error-messages="mixValidationErrors"
                    :available-items="$options.availableWidgets"
                    :copy-clipboard="mixCopyListItemClipboard"
                    :options="$options.options"
                    @list-sort="mixListItemsReassort"
                    @add="mixCreateListItemHandler"
                    @option="mixListItemOptionHandler"
                  >
                    <template #item="{ data }">
                      <component
                        :is="data.item.type"
                        :list-item="data.item"
                        :error-messages="mixDynamicListValidationErrors[data.itemIndex]"
                        @change="mixListItemChangeHandler($event, data.itemIndex)"
                        @change-item="
                          mixListItemChangeItemHandler($event, data.itemIndex)
                        "
                        @remove-item="
                          mixListItemRemoveItemHandler($event, data.itemIndex)
                        "
                      />
                    </template>
                  </DynamicListWrapper>
                </v-card-text>
              </PageContent>
            </v-tab-item>
          </v-tabs-items>
        </v-card>
      </v-col>
      <v-col cols="4">
        <ManageSidePanel
          ref="manageSidePanel"
          isShowSortOrder
          :is-new="!data.id"
          :created-at="data.createdAt"
          :updated-at="data.updatedAt"
          :published-at="data.publishedAt"
          :author="data.author"
          :is-loading="loading"
          :language="language"
          :translations="fields.translations"
          :entity-id="data.id"
          :entity-type="'place'"
          :sortOrder="fields.sortOrder"
          @delete="$emit('delete-place')"
          @cancel="$router.push({ name: 'page-places' })"
          @submit="submit"
          @update-translations="Object.assign(fields.translations, $event)"
          @language-update="$emit('language-update', $event)"
          @update-sort-order="fields.sortOrder = Number($event)"
        >
          <template #info-append>
            <StatusPicker
              align="center"
              cols="12"
              title="Статус:"
              :disabled="loading"
              :loading="loading"
              :status="fields.status"
              @status:change="fields.status = $event"
            />
            <v-row v-if="!isNew" align="center">
              <v-col cols="6">
                <span>Посмотреть на сайте</span>
              </v-col>
              <v-col cols="6">
                <v-btn
                  icon
                  link
                  color="primary"
                  target="_blank"
                  :href="redirerectToWebPortal"
                  :loading="loading"
                  ><v-icon>mdi-web</v-icon></v-btn
                >
              </v-col>
            </v-row>
          </template>
          <template #body>
            <v-card flat>
              <v-card-subtitle class="grey--text"> Связи </v-card-subtitle>
              <v-card-text>
                <v-row no-gutters>
                  <v-col cols="12" v-if="!!cities">
                    <span>Города</span>
                    <v-select
                      v-model="fields.cityId"
                      outlined
                      value="4"
                      item-value="id"
                      item-text="title"
                      clearable
                      :items="cities"
                      :disabled="loading"
                      :loading="loading"
                    />
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col>
                    <span>Исторические личности</span>
                    <v-select
                      outlined
                      no-data-text="Список пуст"
                      item-value="id"
                      item-text="name"
                      clearable
                      multiple
                      :value="fields.historicalFigures"
                      :items="historicalFigures"
                      @change="fields.historicalFigures = $event"
                    />
                  </v-col>
                </v-row>
                <v-row v-if="!!placeCategories" no-gutters>
                  <v-col>
                    <span>Категории мест *</span>
                    <v-select
                      outlined
                      id="categoryIds"
                      no-data-text="Список пуст"
                      item-value="id"
                      item-text="title"
                      multiple
                      :error-messages="validationErrors.categoryIds"
                      :value="fields.categoryIds"
                      :items="placeCategories"
                      @change="fields.categoryIds = $event"
                    />
                    {{ fields.placeCategories }}
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </template>
        </ManageSidePanel>
      </v-col>
    </v-row>
  </form>
</template>
<script>
import FormAbstract from '@/components/abstract/form'
import widgetParentMix from '@/mixins/widgets/widgetParent.mix.js'
import parentDynamicList from '@/mixins/dynamic-list/parentDynamicList.mix.js'
import prepareTranslations from '@/mixins/translations/prepareTranslations.mix'
import validationTranslations from '@/mixins/translations/validationTranslations.mix.js'

import { defaultDateFormat } from '@/configs/date.js'
import tabs from '@/configs/tabs.js'
import { mediaTypesByKeys } from '@/configs/mediaFileTypes'
import { mixForm } from '@/utils/form.mix.js'
import { widgetListForCityAndPlaceList } from '@/utils/widgets'
import Geolocation from '@/utils/types/Geolocation.js'
import Widget from '@/utils/widgets/Widget'
import validationRules from './validation-rules'

import ManageSidePanel from '@/components/manage-side-panel'
import DynamicListWrapper from '@/components/dynamic-list-wrapper/DynamicListWrapper.vue'
import MediaFilePicker from '@/components/media-file-picker'
import LocationAutocomplete from '@/components/location-autocomplete'
import SeoSettings from '@/components/seo-settings'
import TranslationIcon from '@/components/translation-icon/translationIcon'
import FormHeader from '@/components/form-header'
import InputTitleSlug from '@/components/inputs/InputTitleSlug'
import StatusPicker from '@/components/status-picker'
import ProgressCircular from '@/components/progress-circular'

import historicalFiguresQuery from '@/services/gql/types/HistoricalFigures/queries/historicalFiguresGrid.gql'
import placeCategoryQuery from '@/services/gql/types/PlacesCategories/queries/placesCategories.gql'
import PageContent from '@/components/page-content/PageContent.vue'

export default {
  extends: FormAbstract,
  mixins: [
    widgetParentMix,
    parentDynamicList,
    validationTranslations,
    prepareTranslations,
    mixForm
  ],
  name: 'FormPlace',
  mediaTypesByKeys: mediaTypesByKeys(),
  tabs,
  defaultDateFormat,
  validationRules,
  availableWidgets: widgetListForCityAndPlaceList,
  dynamicListName: 'widgets',
  components: {
    PageContent,
    InputTitleSlug,
    TranslationIcon,
    DynamicListWrapper,
    ManageSidePanel,
    MediaFilePicker,
    LocationAutocomplete,
    SeoSettings,
    FormHeader,
    StatusPicker,
    ProgressCircular
  },
  props: {
    data: {
      type: Object,
      default: null
    },
    cities: {
      type: Array,
      default: null
    },
    language: {
      type: String,
      default: null
    },
    isNew: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      activeTab: 0,
      city: {},
      placeCategories: [],
      historicalFigures: [],
      fields: {
        title: null,
        slug: undefined,
        subtitle: null,
        status: null,
        widgets: [],
        imageIds: [],
        categoryIds: [],
        address: new Geolocation(),
        thumbnailId: null,
        cityId: null,
        coverImageId: null,
        seoMetaTitle: null,
        seoMetaDescription: null,
        seoDisallowIndex: false,
        seoMetaKeywords: null,
        seoRelCanonical: null,
        seoOgImageId: null,
        translations: { ru: { isEnabled: true } },
        historicalFigures: null,
        sortOrder: null
      }
    }
  },
  computed: {
    redirerectToWebPortal () {
      if (this.data) {
        const { slug } = this.data
        return window.location.href.includes('demo')
          ? `${process.env.VUE_APP_WEB_PORTAL_URL_DEMO}/places/${this.returnPrimaryCategory}/${slug}`
          : `${process.env.VUE_APP_WEB_PORTAL_URL}/places/${this.returnPrimaryCategory}/${slug}`
      }
      return '#'
    },
    returnPrimaryCategory () {
      if (this.fields?.categoryIds.length) {
        return (
          this.placeCategories?.filter(
            category => category.id === this.fields.categoryIds[0]
          )[0]?.slug || 'no-category'
        )
      }
      return 'no-category'
    },
    validationRulesVars () {
      return {
        id: this.data?.id,
        ctx: this,
        isNew: this.isNew,
        address: this.fields.address
      }
    }
  },
  watch: {
    data (data) {
      this.populate(data)
      this.fields.categoryIds = data?.categories.map(category => category.id) || []
      this.$set(this.fields, 'coverImageId', data?.coverImage?.id || null)
      this.$set(this.fields, 'thumbnailId', data?.thumbnail?.id || null)
      this.$set(this.fields, 'imageIds', data?.images.map(image => image.id))
      this.fields.seoOgImageId = data?.seoOgImage?.id || null
      this.fields.widgets = Widget.populateList(data.widgets)
      this.fields.cityId = data?.city?.id
      this.fields.historicalFigures =
        data?.historicalFigures?.map(figure => figure.id) || null
    }
  },
  methods: {
    prepareImages (images) {
      this.fields.imageIds = images.map(image => image.id)
    },
    updateLocation ({ value, fieldName }) {
      this.fields.address[fieldName] = value
    },
    async submit () {
      const isValid = await this.validate()
      if (!isValid) {
        this.mixScrollToError(this.$v)
        return
      }
      if (!this.mixIsValidTranslations(this.fields.translations)) {
        this.$errorMsg('Не выбран ни один язык для контента!')
        return
      }
      const payload = {
        ...this.composeFormData(this.fields)
      }
      this.mixPrepareTranslations(payload)
      const isWidgetsInValid = this.mixValidateDynamicList(payload.widgets)
      if (!isWidgetsInValid) {
        this.$emit('submit', { data: payload })
      } else {
        this.$errorMsg('Не заполнены обязательные поля виджетов!')
      }
    }
  },
  apollo: {
    placeCategories: {
      fetchPolicy: 'network-only',
      query: placeCategoryQuery,
      variables () {
        return {
          orderBy: 'id_ASC'
        }
      },
      result (res) {
        this.placeCategories = res?.data?.placeCategories.list.map(placeCategory => ({
          id: placeCategory?.id || null,
          title: placeCategory?.title || null,
          slug: placeCategory?.slug || null
        }))
      }
    },
    historicalFigures: {
      fetchPolicy: 'network-only',
      query: historicalFiguresQuery,
      result (res) {
        if (res.data === undefined) {
          this.$handleApiError(res.error)
          this.$router.push({ name: 'page-places' })
        } else {
          this.historicalFigures = res?.data?.historicalFigures?.list || null
        }
      }
    }
  }
}
</script>
